/**
 * The markdown parser of prose (docs/PROSE.md §2, §3): the subset the product
 * reads and writes, parsed into blocks and inline tokens. Pure: no React, no
 * DOM, no I/O. One reading of a prose text for every layer: the kit renderer
 * draws it (`src/components/markdown.tsx`), the prose editor opens on it
 * (`src/components/ui/prose-markdown.ts`), and the references a text names
 * are read through it (`src/domain/references.ts`), so the rest state, the
 * editor and the notification a reference mints never disagree.
 *
 * - blocks: headings `#` to `######`, paragraphs, nested lists (`-` `*` `+`
 *   and `1.` `1)`, mixed by level; an ordered list keeps its first number;
 *   task items `- [ ]` / `- [x]`), blockquotes, fenced code, pipe tables
 *   (alignment markers accepted and ignored), thematic breaks;
 * - inline: code spans first (verbatim), then bold, italic (`_` only at word
 *   boundaries, so `snake_case_name` stays literal), `~~strike~~`, links, bare
 *   http(s) autolinks, references (`[label](person:<id>)`,
 *   `[label](item:<uuid>)`, docs/PROSE.md §5), backslash escapes.
 *
 * **A newline inside a paragraph is a line break**: text typed on a phone
 * ("Milch⏎Eier⏎Brot") reads as the lines it was typed in, and a blank line
 * starts a new paragraph. Everything outside the subset is text.
 *
 * A link is an http(s) URL or a path on this origin (`/items/…`); anything
 * else (`javascript:`, `data:`) stays literal text. A reference is a link
 * whose destination is a person's or an item's ref; its label is text, read
 * with its backslash escapes resolved and no marks.
 *
 * **Never throws, linear in the input**: one pass per line and one pass per
 * inline run; code spans, link brackets and emphasis are matched through
 * precomputed indexes and per-character opener stacks, never by searching
 * forward from every candidate.
 */

// ---------------------------------------------------------------- blocks ---

export interface MarkdownList {
  kind: 'list';
  ordered: boolean;
  /** The first item's number (1 for an unordered list). */
  start: number;
  items: ListItem[];
  /** The first item's marker when it is not the usual one (`*` or `+` for a
   *  bullet, `)` after a number): not rendered, kept so the editor writes the
   *  list back as it was written. */
  marker?: '*' | '+' | ')';
}

export interface ListItem {
  /** A task item's box: false open, true checked; null for a plain item. */
  task: boolean | null;
  /** In source order: the item's text (a continuation line joins it with a
   *  line break; a blank line starts the next text), a quote, a child list. */
  content: ListContent[];
}

export type ListContent =
  | { kind: 'text'; text: string }
  | { kind: 'quote'; lines: string[] }
  | MarkdownList;

export type MarkdownBlock =
  | { kind: 'heading'; level: 1 | 2 | 3 | 4 | 5 | 6; text: string }
  /** The lines of the paragraph joined by `\n`; each renders as a line break. */
  | { kind: 'paragraph'; text: string }
  /** The quote's lines with their `>` removed; a blank one splits paragraphs. */
  | { kind: 'quote'; lines: string[] }
  /** A fenced block's lines, and the info string after its opening fence
   *  (a language name, kept so the editor writes it back; not rendered). */
  | { kind: 'code'; lines: string[]; info?: string }
  | MarkdownList
  | { kind: 'table'; header: string[]; rows: string[][] }
  | { kind: 'rule' };

/** Lists nest at most this deep; a deeper item is a sibling at the deepest
 *  level, so pathological indentation cannot grow the tree without bound. */
const MAX_LIST_DEPTH = 16;

const FENCE = /^ {0,3}```/;
const HEADING = /^ {0,3}(#{1,6})[ \t]+(\S.*)$/;
const RULE = /^ {0,3}([-*_])[ \t]*(?:\1[ \t]*){2,}$/;
const QUOTE = /^ {0,3}>/;
const QUOTE_MARK = /^ {0,3}> ?/;
const LIST_ITEM = /^([ \t]*)([-*+]|\d{1,9}[.)])(?:[ \t]+(.*))?$/;
const TASK = /^\[([ xX])\](?:[ \t]+|$)/;
const SEPARATOR_CELL = /^:?-+:?$/;

const isBlank = (line: string) => line.trim() === '';

/** The column a line's text starts at; a tab advances to the next stop of 4. */
function indentOf(line: string): number {
  let col = 0;
  for (const ch of line) {
    if (ch === ' ') col += 1;
    else if (ch === '\t') col += 4 - (col % 4);
    else break;
  }
  return col;
}

/** A heading's text without a closing run of `#`. */
function headingText(raw: string): string {
  const body = raw.trimEnd();
  let k = body.length;
  while (k > 0 && body[k - 1] === '#') k -= 1;
  if (k < body.length && (k === 0 || body[k - 1] === ' ' || body[k - 1] === '\t')) {
    return body.slice(0, k).trimEnd();
  }
  return body;
}

interface ItemLine {
  indent: number;
  ordered: boolean;
  start: number;
  /** The bullet (`-` `*` `+`) or the character after the number (`.` `)`). */
  marker: string;
  task: boolean | null;
  text: string;
}

function itemLine(line: string): ItemLine | null {
  if (RULE.test(line)) return null; // `- - -` is a thematic break, not an item
  const m = LIST_ITEM.exec(line);
  if (!m) return null;
  const marker = m[2]!;
  const ordered = marker !== '-' && marker !== '*' && marker !== '+';
  let text = (m[3] ?? '').trim();
  let task: boolean | null = null;
  const box = TASK.exec(text);
  if (box) {
    task = box[1] !== ' ';
    text = text.slice(box[0].length).trim();
  }
  return {
    indent: indentOf(m[1]!),
    ordered,
    start: ordered ? Number.parseInt(marker, 10) : 1,
    marker: marker.slice(-1),
    task,
    text,
  };
}

/** A table row's cells: outer pipes dropped, split on unescaped pipes, `\|`
 *  kept as a literal pipe. One pass over the row. */
function tableCells(row: string): string[] {
  let s = row.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);
  const cells: string[] = [];
  let cell = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (ch === '\\' && s[i + 1] === '|') {
      cell += '|';
      i += 1;
    } else if (ch === '|') {
      cells.push(cell.trim());
      cell = '';
    } else {
      cell += ch;
    }
  }
  cells.push(cell.trim());
  return cells;
}

/** The header's cell count when lines[i] opens a table (a row with a pipe,
 *  then a separator row of as many `---` cells, itself holding a pipe), or 0. */
function tableAt(lines: readonly string[], i: number): number {
  const head = lines[i];
  const sep = lines[i + 1];
  if (head === undefined || sep === undefined) return 0;
  if (!head.includes('|') || !sep.includes('|')) return 0;
  const sepCells = tableCells(sep);
  if (!sepCells.every((c) => SEPARATOR_CELL.test(c))) return 0;
  const width = tableCells(head).length;
  return width === sepCells.length ? width : 0;
}

/** Whether lines[i] opens a block other than a paragraph. Inside a paragraph
 *  an ordered item interrupts only when it starts at 1, and a bullet only
 *  with text, so "We meet on the\n10. of October" stays one paragraph. */
function opensBlock(lines: readonly string[], i: number): boolean {
  const line = lines[i]!;
  if (FENCE.test(line) || HEADING.test(line) || QUOTE.test(line) || RULE.test(line)) return true;
  if (tableAt(lines, i) > 0) return true;
  const item = itemLine(line);
  if (!item) return false;
  return item.ordered ? item.start === 1 : item.text !== '' || item.task !== null;
}

interface OpenList {
  list: MarkdownList;
  /** The marker column of the list's latest item. */
  itemIndent: number;
}

function newItem(item: ItemLine): ListItem {
  return {
    task: item.task,
    content: item.text === '' ? [] : [{ kind: 'text', text: item.text }],
  };
}

function newList(item: ItemLine): MarkdownList {
  const list: MarkdownList = {
    kind: 'list',
    ordered: item.ordered,
    start: item.start,
    items: [newItem(item)],
  };
  if (item.marker === '*' || item.marker === '+' || item.marker === ')') list.marker = item.marker;
  return list;
}

const lastItem = (list: MarkdownList): ListItem => list.items[list.items.length - 1]!;

/**
 * A list from its first item at lines[i]. An item indented two or more
 * columns past the latest item's marker opens a child list; a line indented
 * past an item's marker joins the deepest such item; a blank line keeps the
 * list when the next line is an item or indented under one; a line that is
 * neither ends it. Returns the list and the line after it.
 */
function parseList(lines: readonly string[], i: number): [MarkdownList, number] {
  const first = itemLine(lines[i]!)!;
  const root = newList(first);
  const stack: OpenList[] = [{ list: root, itemIndent: first.indent }];
  let blankBefore = false;
  i += 1;
  while (i < lines.length) {
    const line = lines[i]!;
    if (isBlank(line)) {
      blankBefore = true;
      i += 1;
      continue;
    }
    const item = itemLine(line);
    if (item) {
      // Close the lists this item sits outside of.
      while (stack.length > 1) {
        const top = stack[stack.length - 1]!;
        const parent = stack[stack.length - 2]!;
        if (item.indent >= top.itemIndent || item.indent >= parent.itemIndent + 2) break;
        stack.pop();
      }
      const top = stack[stack.length - 1]!;
      if (item.indent >= top.itemIndent + 2 && stack.length < MAX_LIST_DEPTH) {
        const child = newList(item);
        lastItem(top.list).content.push(child);
        stack.push({ list: child, itemIndent: item.indent });
      } else if (item.ordered !== top.list.ordered) {
        // A marker of the other kind at this level starts a new list there.
        if (stack.length === 1) break;
        stack.pop();
        const parent = stack[stack.length - 1]!;
        const child = newList(item);
        lastItem(parent.list).content.push(child);
        stack.push({ list: child, itemIndent: item.indent });
      } else {
        top.list.items.push(newItem(item));
        top.itemIndent = item.indent;
      }
      blankBefore = false;
      i += 1;
      continue;
    }
    // Not an item: it joins the deepest item whose marker it is indented past.
    const indent = indentOf(line);
    let level = stack.length - 1;
    while (level >= 0 && stack[level]!.itemIndent >= indent) level -= 1;
    if (level < 0) break;
    stack.length = level + 1;
    const content = lastItem(stack[level]!.list).content;
    const last = content[content.length - 1];
    const body = line.trim();
    if (QUOTE.test(body)) {
      const quoted = body.replace(QUOTE_MARK, '');
      if (last?.kind === 'quote' && !blankBefore) last.lines.push(quoted);
      else content.push({ kind: 'quote', lines: [quoted] });
    } else if (last?.kind === 'text' && !blankBefore) {
      last.text += `\n${body}`;
    } else {
      content.push({ kind: 'text', text: body });
    }
    blankBefore = false;
    i += 1;
  }
  return [root, i];
}

/** Split markdown text into blocks. Pure; line-oriented; never throws. */
export function parseMarkdownBlocks(text: string): MarkdownBlock[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (isBlank(line)) {
      i += 1;
      continue;
    }
    // Fenced code: everything verbatim until the closing fence (or the end).
    if (FENCE.test(line)) {
      const info = line.replace(FENCE, '').trim();
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i]!)) {
        body.push(lines[i]!);
        i += 1;
      }
      i += 1; // the closing fence, when there is one
      blocks.push(
        info === '' ? { kind: 'code', lines: body } : { kind: 'code', lines: body, info },
      );
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      const level = heading[1]!.length as 1 | 2 | 3 | 4 | 5 | 6;
      blocks.push({ kind: 'heading', level, text: headingText(heading[2]!) });
      i += 1;
      continue;
    }
    if (RULE.test(line)) {
      blocks.push({ kind: 'rule' });
      i += 1;
      continue;
    }
    if (QUOTE.test(line)) {
      const body: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i]!)) {
        body.push(lines[i]!.replace(QUOTE_MARK, ''));
        i += 1;
      }
      blocks.push({ kind: 'quote', lines: body });
      continue;
    }
    const width = tableAt(lines, i);
    if (width > 0) {
      const fit = (cells: string[]) => Array.from({ length: width }, (_, k) => cells[k] ?? '');
      const header = fit(tableCells(line));
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && !isBlank(lines[i]!) && lines[i]!.includes('|')) {
        rows.push(fit(tableCells(lines[i]!)));
        i += 1;
      }
      blocks.push({ kind: 'table', header, rows });
      continue;
    }
    if (itemLine(line)) {
      const [list, next] = parseList(lines, i);
      blocks.push(list);
      i = next;
      continue;
    }
    // A paragraph: lines up to a blank line or another block, each trimmed
    // (leading spaces are not kept; an indented line is no code block) and
    // each kept on its own line.
    const body: string[] = [line.trim()];
    i += 1;
    while (i < lines.length && !isBlank(lines[i]!) && !opensBlock(lines, i)) {
      body.push(lines[i]!.trim());
      i += 1;
    }
    blocks.push({ kind: 'paragraph', text: body.join('\n') });
  }
  return blocks;
}

/** A quote's lines as paragraphs: a blank line between, every other newline
 *  kept as a line break. */
export function quoteParagraphs(lines: readonly string[]): string[] {
  const paragraphs: string[] = [];
  let current: string[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (line === '') {
      if (current.length > 0) paragraphs.push(current.join('\n'));
      current = [];
    } else {
      current.push(line);
    }
  }
  if (current.length > 0) paragraphs.push(current.join('\n'));
  return paragraphs;
}

/** Text as one paragraph of inline marks (the note): each line trimmed and
 *  kept, the whole trimmed, no block structure. */
export function inlineParagraph(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .join('\n')
    .trim();
}

// ------------------------------------------------- the serializer's rules ---
// The prose editor writes markdown this parser reads back
// (src/components/ui/prose-markdown.ts). Where a line it writes could be read
// as structure, it escapes it, and it asks these questions to know where.

/**
 * Whether lines[i] would open a block other than a paragraph: `first` for a
 * paragraph's first line (any block opener, as the block pass reads it), else
 * for a line inside a paragraph (the narrower rule that lets "We meet on
 * the⏎10. of October" stay one paragraph).
 */
export function lineOpensBlock(lines: readonly string[], i: number, first: boolean): boolean {
  if (!first) return opensBlock(lines, i);
  const line = lines[i]!;
  return (
    FENCE.test(line) ||
    HEADING.test(line) ||
    RULE.test(line) ||
    QUOTE.test(line) ||
    tableAt(lines, i) > 0 ||
    itemLine(line) !== null
  );
}

/** Whether a line under a list item, its indentation removed, would leave the
 *  item's text: a list item of its own, or a quote. */
export function lineLeavesItemText(line: string): boolean {
  return itemLine(line) !== null || QUOTE.test(line);
}

/** Whether a line is a thematic break (`---`, `* * *`). */
export function isRuleLine(line: string): boolean {
  return RULE.test(line);
}

/** Whether a list item's text starts with a task box (`[ ] `, `[x]`). */
export function startsWithTaskBox(text: string): boolean {
  return TASK.test(text);
}

/** Whether a heading's text ends in a run of `#` that the heading line would
 *  drop as its closing sequence. */
export function endsWithClosingHashes(text: string): boolean {
  return headingText(text) !== text.trimEnd();
}

// ---------------------------------------------------------------- inline ---

/** Inline tokens, a tree since marks nest (`**bold `code`**`). A `break` is a
 *  newline inside the paragraph. */
export type InlineToken =
  | { kind: 'text'; text: string }
  | { kind: 'break' }
  | { kind: 'code'; text: string }
  | { kind: 'bold'; children: InlineToken[] }
  | { kind: 'italic'; children: InlineToken[] }
  | { kind: 'strike'; children: InlineToken[] }
  /** `image` marks one written `![alt](url)`: it renders as a link all the
   *  same, and the editor writes it back as it was. */
  | { kind: 'link'; href: string; children: InlineToken[]; image?: true }
  /** A reference to a person or an item (docs/PROSE.md §5): the ref its
   *  destination names, and its label as plain text, escapes resolved. */
  | ({ kind: 'reference'; label: string } & ReferenceTarget);

type MarkKind = 'bold' | 'italic' | 'strike';
type DelimChar = '*' | '_' | '~';

/** A run of `*`, `_` or `~~` that may open or close a mark. `opens` and
 *  `closes` are the marks it took part in, in the order they matched. */
interface Delim {
  ch: DelimChar;
  idx: number;
  remaining: number;
  opens: MarkKind[];
  closes: MarkKind[];
}

type Flat =
  | { t: 'text'; s: string }
  | { t: 'break' }
  | { t: 'code'; s: string }
  | { t: 'link'; href: string; children: InlineToken[]; image?: true }
  | ({ t: 'reference'; label: string } & ReferenceTarget)
  | { t: 'delim'; d: Delim };

/** Marks nest at most this deep; a deeper pair renders as its literal text. */
const MAX_MARK_DEPTH = 24;

const ASCII_PUNCT = /[!-/:-@[-`{-~]/;
const isSpace = (ch: string | undefined) => ch === undefined || /\s/.test(ch);
const isWordChar = (ch: string | undefined) => ch !== undefined && /[\p{L}\p{N}]/u.test(ch);
/** Where a bare URL or a link destination stops. */
const URL_STOP = /[\s<>`[\]]/;
/** Trailing characters a bare URL never ends on ("see https://x.test."). */
const URL_TRAIL = new Set(['.', ',', ')', ';', ':', '!', '?', '*', '_', '~', "'", '"']);
const WEB_URL = /^https?:\/\/[^/]/;

const MARK_TEXT: Record<MarkKind, (ch: DelimChar) => string> = {
  bold: (ch) => ch + ch,
  italic: (ch) => ch,
  strike: () => '~~',
};

/** A link's destination as the scan reads it: the URL (or a reference's ref),
 *  and the index just past its closing parenthesis. */
export interface LinkDestination {
  href: string;
  end: number;
}

// ------------------------------------------------------------ references ---

/** What a reference names (docs/PROSE.md §5): a person or an item. */
export type ReferenceKind = 'person' | 'item';

/** The target of a reference: its kind and the id its ref carries. */
export interface ReferenceTarget {
  ref: ReferenceKind;
  id: string;
}

/** A person's id as a reference names it: a uuid, or a seeded persona's
 *  slug (`ari`). Letters, digits, `_` `.` `-`, starting with a letter or
 *  digit. */
const PERSON_ID = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,127}$/;
const ITEM_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The target of a reference's destination (`person:<id>` or `item:<uuid>`,
 * the ref encoding of `src/domain/refs.ts` as a link scheme), or null for any
 * other href. Pure.
 */
export function parseReference(href: string): ReferenceTarget | null {
  const colon = href.indexOf(':');
  if (colon < 0) return null;
  const scheme = href.slice(0, colon);
  const id = href.slice(colon + 1);
  if (scheme === 'person' && PERSON_ID.test(id)) return { ref: 'person', id };
  if (scheme === 'item' && ITEM_ID.test(id)) return { ref: 'item', id };
  return null;
}

/** A reference's label as stored: every backslash, `[`, `]` and backtick
 *  escaped, so the label reads back as written and never opens a link or a
 *  code span; a line break becomes a space, a label being one line. */
export function escapeReferenceLabel(label: string): string {
  return label.replace(/\r\n?|\n/g, ' ').replace(/[\\[\]`]/g, '\\$&');
}

/** A label as written between the brackets, its backslash escapes resolved. */
function unescapeLabel(raw: string): string {
  return raw.replace(/\\([!-/:-@[-`{-~])/g, '$1');
}

/**
 * A reference in the stored form (docs/PROSE.md §5.2): `[label](person:<id>)`
 * or `[label](item:<uuid>)`, the label escaped (`escapeReferenceLabel`). The
 * one spelling the editor writes and the parser reads back.
 */
export function referenceMarkdown(target: ReferenceTarget, label: string): string {
  return `[${escapeReferenceLabel(label)}](${target.ref}:${target.id})`;
}

/**
 * The destination of a link whose `(` sits just before `start`, or null: an
 * http(s) URL, a path on this origin (`/…`, not `//…`), or a reference's ref
 * (`person:<id>`, `item:<uuid>`), closed by `)` before any space or `<>`[]`
 * character. Anything else leaves the brackets literal text. The prose
 * editor's serializer asks the same question of the text it writes
 * (src/components/ui/prose-markdown.ts).
 */
export function linkDestinationAt(src: string, start: number): LinkDestination | null {
  const web = src.startsWith('https://', start) || src.startsWith('http://', start);
  const local = src[start] === '/' && src[start + 1] !== '/';
  const reference = src.startsWith('person:', start) || src.startsWith('item:', start);
  if (!web && !local && !reference) return null;
  let e = start;
  while (e < src.length && src[e] !== ')' && !URL_STOP.test(src[e]!)) e += 1;
  const href = src.slice(start, e);
  if (src[e] !== ')') return null;
  if (reference) return parseReference(href) ? { href, end: e + 1 } : null;
  return local || WEB_URL.test(href) ? { href, end: e + 1 } : null;
}

/** Parse inline markdown into a token tree. Pure; never throws; linear. */
export function parseInline(text: string): InlineToken[] {
  return scanInline(text, true);
}

function scanInline(src: string, links: boolean): InlineToken[] {
  const n = src.length;

  // Backslash escapes, decided once left to right (`\\*` is a backslash and a
  // live star). escaped[k] marks a character a backslash made literal.
  const escaped = new Uint8Array(n + 1);
  for (let k = 0; k < n - 1; k++) {
    if (src[k] === '\\' && !escaped[k] && ASCII_PUNCT.test(src[k + 1]!)) escaped[k + 1] = 1;
  }

  // A code span closes on a raw backtick run of the opener's exact length:
  // the runs' positions by length, walked by a forward-only cursor each.
  const runsByLength = new Map<number, number[]>();
  for (let k = 0; k < n; ) {
    if (src[k] !== '`') {
      k += 1;
      continue;
    }
    let e = k;
    while (e < n && src[e] === '`') e += 1;
    const list = runsByLength.get(e - k);
    if (list) list.push(k);
    else runsByLength.set(e - k, [k]);
    k = e;
  }
  const cursors = new Map<number, number>();
  const closerFor = (length: number, from: number): number => {
    const list = runsByLength.get(length);
    if (!list) return -1;
    let c = cursors.get(length) ?? 0;
    while (c < list.length && list[c]! < from) c += 1;
    cursors.set(length, c);
    return c < list.length ? list[c]! : -1;
  };

  // The next unescaped `]` from every position, and each `]`'s destination
  // parsed once, so a run of `[` never rescans the rest of the text.
  const nextClose = new Int32Array(n + 1).fill(-1);
  if (links) {
    for (let k = n - 1; k >= 0; k--) {
      nextClose[k] = src[k] === ']' && !escaped[k] ? k : nextClose[k + 1]!;
    }
  }
  const destinations = new Map<number, LinkDestination | null>();
  const destinationAfter = (close: number) => {
    const known = destinations.get(close);
    if (known !== undefined) return known;
    const found = src[close + 1] === '(' ? linkDestinationAt(src, close + 2) : null;
    destinations.set(close, found);
    return found;
  };

  const flat: Flat[] = [];
  let buf = '';
  const flush = () => {
    if (buf !== '') flat.push({ t: 'text', s: buf });
    buf = '';
  };
  const stacks: Record<DelimChar, Delim[]> = { '*': [], _: [], '~': [] };
  const others: Record<DelimChar, DelimChar[]> = {
    '*': ['_', '~'],
    _: ['*', '~'],
    '~': ['*', '_'],
  };

  let i = 0;
  while (i < n) {
    const ch = src[i]!;
    if (ch === '\\' && escaped[i + 1]) {
      buf += src[i + 1];
      i += 2;
      continue;
    }
    if (ch === '\n') {
      flush();
      flat.push({ t: 'break' });
      i += 1;
      continue;
    }
    if (ch === '`') {
      let e = i;
      while (e < n && src[e] === '`') e += 1;
      const length = e - i;
      const close = closerFor(length, e);
      if (close < 0) {
        buf += src.slice(i, e);
        i = e;
        continue;
      }
      let code = src.slice(e, close).replace(/\n/g, ' ');
      if (code.length > 1 && code.startsWith(' ') && code.endsWith(' ') && code.trim() !== '') {
        code = code.slice(1, -1);
      }
      flush();
      flat.push({ t: 'code', s: code });
      i = close + length;
      continue;
    }
    if (links && (ch === '[' || (ch === '!' && src[i + 1] === '['))) {
      const open = ch === '!' ? i + 1 : i;
      const close = nextClose[open + 1]!;
      const dest = close >= 0 ? destinationAfter(close) : null;
      const target = dest ? parseReference(dest.href) : null;
      if (dest && target && ch === '!') {
        // A reference is never an image: the `!` is text, and the `[` after
        // it opens the reference on the next step.
        buf += ch;
        i += 1;
        continue;
      }
      if (dest && target) {
        flush();
        flat.push({ t: 'reference', ...target, label: unescapeLabel(src.slice(open + 1, close)) });
        i = dest.end;
        continue;
      }
      if (dest) {
        // An image is a link to its URL with the alt text: never fetched.
        const label = src.slice(open + 1, close);
        const children = label.trim() === '' ? [] : scanInline(label, false);
        flush();
        flat.push({
          t: 'link',
          href: dest.href,
          children: children.length > 0 ? children : [{ kind: 'text', text: dest.href }],
          ...(ch === '!' ? { image: true as const } : {}),
        });
        i = dest.end;
        continue;
      }
    }
    if (
      links &&
      ch === 'h' &&
      !isWordChar(src[i - 1]) &&
      (src.startsWith('https://', i) || src.startsWith('http://', i))
    ) {
      let e = i;
      let opened = 0;
      let closed = 0;
      while (e < n && !URL_STOP.test(src[e]!)) {
        if (src[e] === '(') opened += 1;
        else if (src[e] === ')') closed += 1;
        e += 1;
      }
      // Trailing punctuation is the sentence's, not the URL's; a closing
      // paren stays when the URL opened one ("…/Foo_(bar)").
      while (e > i && URL_TRAIL.has(src[e - 1]!)) {
        if (src[e - 1] === ')') {
          if (closed <= opened) break;
          closed -= 1;
        }
        e -= 1;
      }
      const href = src.slice(i, e);
      if (WEB_URL.test(href)) {
        flush();
        flat.push({ t: 'link', href, children: [{ kind: 'text', text: href }] });
        i = e;
        continue;
      }
    }
    if (ch === '*' || ch === '_' || ch === '~') {
      let e = i;
      while (e < n && src[e] === ch) e += 1;
      const length = e - i;
      const prev = src[i - 1];
      const next = src[e];
      let canOpen = !isSpace(next);
      let canClose = !isSpace(prev);
      if (ch === '_') {
        // Only at a word boundary: `snake_case_name` stays literal.
        canOpen = canOpen && !isWordChar(prev);
        canClose = canClose && !isWordChar(next);
      }
      if ((ch === '~' && length !== 2) || (!canOpen && !canClose)) {
        buf += src.slice(i, e);
        i = e;
        continue;
      }
      flush();
      const d: Delim = { ch, idx: flat.length, remaining: length, opens: [], closes: [] };
      flat.push({ t: 'delim', d });
      if (canClose) {
        const stack = stacks[ch];
        while (d.remaining > 0 && stack.length > 0) {
          const o = stack[stack.length - 1]!;
          // A mark of another character opened inside this pair can no longer
          // close after it.
          for (const other of others[ch]) {
            const s = stacks[other];
            while (s.length > 0 && s[s.length - 1]!.idx > o.idx) s.pop();
          }
          const use = ch === '~' ? 2 : o.remaining >= 2 && d.remaining >= 2 ? 2 : 1;
          const kind: MarkKind = ch === '~' ? 'strike' : use === 2 ? 'bold' : 'italic';
          o.remaining -= use;
          d.remaining -= use;
          o.opens.push(kind);
          d.closes.push(kind);
          if (o.remaining === 0) stack.pop();
        }
      }
      if (canOpen && d.remaining > 0) stacks[ch].push(d);
      i = e;
      continue;
    }
    buf += ch;
    i += 1;
  }
  flush();
  return buildTree(flat);
}

/** The flat scan as a tree. A run renders its closes first (its leading
 *  characters), then what it did not use as text, then its opens outermost
 *  first (its trailing characters). Matched pairs nest by construction. */
function buildTree(flat: readonly Flat[]): InlineToken[] {
  interface Frame {
    kind: MarkKind;
    ch: DelimChar;
    /** Past the depth cap a pair is literal text and owns no children. */
    literal: boolean;
    children: InlineToken[];
  }
  const root: InlineToken[] = [];
  const frames: Frame[] = [];
  /** The children list text goes into: the innermost frame that is a mark. */
  const targets: InlineToken[][] = [root];
  const out = () => targets[targets.length - 1]!;
  const text = (s: string) => {
    if (s === '') return;
    const into = out();
    const last = into[into.length - 1];
    if (last?.kind === 'text') last.text += s;
    else into.push({ kind: 'text', text: s });
  };

  for (const item of flat) {
    switch (item.t) {
      case 'text':
        text(item.s);
        break;
      case 'break':
        out().push({ kind: 'break' });
        break;
      case 'code':
        out().push({ kind: 'code', text: item.s });
        break;
      case 'reference':
        out().push({ kind: 'reference', ref: item.ref, id: item.id, label: item.label });
        break;
      case 'link':
        out().push(
          item.image
            ? { kind: 'link', href: item.href, children: item.children, image: true }
            : { kind: 'link', href: item.href, children: item.children },
        );
        break;
      case 'delim': {
        const d = item.d;
        for (const kind of d.closes) {
          const frame = frames[frames.length - 1];
          if (!frame || frame.kind !== kind) {
            // Unreachable while pairs nest; a slip shows as text.
            text(MARK_TEXT[kind](d.ch));
            continue;
          }
          frames.pop();
          if (frame.literal) {
            text(MARK_TEXT[kind](d.ch));
          } else {
            targets.pop();
            out().push({ kind, children: frame.children });
          }
        }
        text(d.ch.repeat(d.remaining));
        for (let k = d.opens.length - 1; k >= 0; k--) {
          const kind = d.opens[k]!;
          const literal = targets.length > MAX_MARK_DEPTH;
          const frame: Frame = { kind, ch: d.ch, literal, children: [] };
          if (literal) text(MARK_TEXT[kind](d.ch));
          frames.push(frame);
          if (!literal) targets.push(frame.children);
        }
        break;
      }
    }
  }
  // Every open has its close; this only guards a parser slip.
  while (frames.length > 0) {
    const frame = frames.pop()!;
    if (frame.literal) continue;
    targets.pop();
    text(MARK_TEXT[frame.kind](frame.ch));
    for (const child of frame.children) out().push(child);
  }
  return root;
}
